import { AUTH_CONNECTION } from "@web3auth/auth";
import { jwtDecode } from "jwt-decode";
import log from "loglevel";
import type { LocationQuery } from "vue-router";

import {
  clearAlternateAuthSessions,
  clearPendingLoginQuery,
  getPendingLoginQuery,
  getStoredAuthConnection,
  setPasswordlessIdToken,
  setPendingLoginQuery,
  setStoredAuthConnection,
} from "@/store/modules/authStorage";
import type { DashboardAuthUser } from "@/utils/interface";

import { getAuth0Connection } from "../auth0Connections";
import { isStaleIssuer } from "./issuer";
import {
  createPasswordlessCaptcha,
  type PasswordlessCaptchaHandle,
  type PasswordlessCaptchaInit,
  type PasswordlessCaptchaWidget,
  promisifyParseHash,
  promisifyPasswordlessLogin,
  promisifyPasswordlessStart,
  waitForPasswordlessCaptchaReload,
} from "./passwordlessClient";
import {
  cancelScheduledRenewal,
  getValidPasswordlessIdToken,
  initPasswordlessAutoRenew,
  isPasswordlessAuthConnection,
  scheduleProactiveRenewal,
  shouldAttemptPasswordlessRenewal,
} from "./sessionRenewal";
import { clearSocialSpaSession, getAuth0Client } from "./spaClient";
import { buildDashboardAuthUser, buildDashboardAuthUserFromIdToken, inferAuthConnectionFromSub, resolveAuthConnection } from "./userMapper";

export type Auth0AppState = {
  query?: LocationQuery;
  authConnection?: string;
};

export type { PasswordlessCaptchaWidget };
export { buildDashboardAuthUser, getAuth0Client };

/**
 * After an Auth0 custom-domain switch, the SPA SDK cache may still hold an id token whose `iss`
 * points at the old domain. The refresh token is tenant-scoped, so forcing a silent exchange
 * against the new domain re-mints tokens with the correct issuer without a full redirect login.
 * Clears the SPA session and returns null when refresh fails (expired/missing refresh token).
 */
async function refreshStaleSocialIdTokenClaims(): Promise<Awaited<ReturnType<Awaited<ReturnType<typeof getAuth0Client>>["getIdTokenClaims"]>>> {
  const client = await getAuth0Client();
  try {
    await client.getTokenSilently({ cacheMode: "off" });
    const claims = await client.getIdTokenClaims();
    if (!claims?.__raw || isStaleIssuer(claims.__raw)) {
      await clearSocialSpaSession();
      return null;
    }
    return claims;
  } catch (error) {
    log.error("Failed to refresh stale Auth0 issuer:", error);
    await clearSocialSpaSession();
    return null;
  }
}

class Auth0Handler {
  async login(authConnection: string, options: { appState?: Auth0AppState } = {}): Promise<void> {
    if (!isPasswordlessAuthConnection(authConnection)) {
      clearAlternateAuthSessions();
    }
    // loginWithRedirect persists appState via the SPA SDK (OAuth state + localStorage), but that
    // cache can be cleared before /end (Safari ITP, etc.). Mirror query to sessionStorage as fallback.
    const query = options.appState?.query;
    if (query && Object.keys(query).length > 0) {
      setPendingLoginQuery(query);
    }
    const client = await getAuth0Client();
    await client.loginWithRedirect({
      authorizationParams: {
        connection: getAuth0Connection(authConnection),
      },
      appState: {
        authConnection,
        ...options.appState,
      },
    });
  }

  createPasswordlessCaptcha(element: HTMLElement): PasswordlessCaptchaHandle {
    return createPasswordlessCaptcha(element);
  }

  waitForPasswordlessCaptchaReload(widget: PasswordlessCaptchaWidget, element: HTMLElement): Promise<PasswordlessCaptchaInit> {
    return waitForPasswordlessCaptchaReload(widget, element);
  }

  async passwordlessStart(email: string, captcha?: string): Promise<void> {
    await promisifyPasswordlessStart(email.trim().toLowerCase(), captcha);
  }

  async passwordlessLogin(email: string, code: string, query: LocationQuery = {}): Promise<void> {
    setStoredAuthConnection(AUTH_CONNECTION.EMAIL_PASSWORDLESS);
    // auth0-js passwordlessLogin redirects with tokens in the hash — no SPA SDK appState.
    setPendingLoginQuery(query);
    await promisifyPasswordlessLogin(email.trim().toLowerCase(), code.trim());
  }

  async handleRedirectCallback(): Promise<{ user: DashboardAuthUser; appState: Auth0AppState }> {
    const client = await getAuth0Client();
    const result = await client.handleRedirectCallback<Auth0AppState>();
    const authUser = await client.getUser();
    const claims = await client.getIdTokenClaims();
    if (!authUser || !claims) {
      throw new Error("Could not load Auth0 user after redirect");
    }

    // appState is the authoritative per-transaction carrier; the shared localStorage value is only
    // a fallback (it can be stale across tabs) and is reconciled against the token's sub.
    const authConnection = resolveAuthConnection(result.appState?.authConnection, getStoredAuthConnection(), claims.sub);
    if (!authConnection) {
      throw new Error("Missing authConnection in Auth0 app state");
    }

    const pendingQuery = getPendingLoginQuery();
    const query = result.appState?.query ?? (Object.keys(pendingQuery).length > 0 ? pendingQuery : undefined);
    clearPendingLoginQuery();

    // Social callback succeeded; ensure no stale bridge or passwordless session remains.
    clearAlternateAuthSessions();
    return {
      user: buildDashboardAuthUser(authConnection, authUser, claims),
      appState: {
        ...result.appState,
        authConnection,
        query,
      },
    };
  }

  async handlePasswordlessRedirectCallback(): Promise<{ user: DashboardAuthUser; appState: Auth0AppState } | null> {
    const hash = window.location.hash;
    if (!hash || (!hash.includes("id_token") && !hash.includes("access_token"))) {
      return null;
    }

    const authResult = await promisifyParseHash(hash);
    if (!authResult?.idToken) {
      return null;
    }

    // No Auth0 appState on this path, so there is no trusted per-transaction value; reconcile the
    // shared (possibly stale cross-tab) stored connection against the token's sub.
    const tokenSub = jwtDecode<{ sub?: string }>(authResult.idToken).sub;
    const authConnection = resolveAuthConnection(undefined, getStoredAuthConnection(), tokenSub) ?? AUTH_CONNECTION.EMAIL_PASSWORDLESS;
    // No Auth0 appState on this path — query was stashed in passwordlessLogin().
    const query = getPendingLoginQuery();
    // Passwordless callback succeeded; drop any lingering SPA SDK social session so it can't shadow
    // this token once it expires (mirrors handleRedirectCallback clearing the passwordless session).
    await clearSocialSpaSession();
    setPasswordlessIdToken(authResult.idToken);
    scheduleProactiveRenewal(authResult.idToken);
    clearPendingLoginQuery();

    return {
      user: buildDashboardAuthUserFromIdToken(authConnection, authResult.idToken),
      appState: {
        authConnection,
        query,
      },
    };
  }

  async isAuthenticated(): Promise<boolean> {
    const passwordlessToken = await getValidPasswordlessIdToken();
    if (passwordlessToken) {
      return true;
    }

    const client = await getAuth0Client();
    return client.isAuthenticated();
  }

  async checkSession(): Promise<void> {
    const client = await getAuth0Client();
    await client.checkSession();
  }

  async getDashboardUser(authConnection: string): Promise<DashboardAuthUser | null> {
    const client = await getAuth0Client();
    if (!(await client.isAuthenticated())) {
      return null;
    }
    const authUser = await client.getUser();
    const claims = await client.getIdTokenClaims();
    if (!authUser || !claims) {
      return null;
    }
    return buildDashboardAuthUser(authConnection, authUser, claims);
  }

  async getIdToken(): Promise<string> {
    const passwordlessToken = await getValidPasswordlessIdToken();
    if (passwordlessToken) {
      return passwordlessToken;
    }

    const client = await getAuth0Client();
    // getTokenSilently() renews via refresh token (or checkSession fallback) before we read __raw,
    // so authenticated API calls never send an expired id token.
    await client.getTokenSilently();
    let claims = await client.getIdTokenClaims();
    if (claims?.__raw && isStaleIssuer(claims.__raw)) {
      claims = await refreshStaleSocialIdTokenClaims();
    }
    if (!claims?.__raw || isStaleIssuer(claims.__raw)) {
      throw new Error("No Auth0 id token found");
    }
    return claims.__raw;
  }

  async logout(options: { returnTo?: string } = {}): Promise<void> {
    cancelScheduledRenewal();
    clearAlternateAuthSessions();

    const client = await getAuth0Client();
    if (await client.isAuthenticated()) {
      await client.logout({
        logoutParams: {
          returnTo: options.returnTo ?? window.location.origin,
        },
      });
      return;
    }

    window.location.href = options.returnTo ?? window.location.origin;
  }

  /** Infer auth connection from Auth0 `sub` when rehydrating without stored connection. */
  inferAuthConnectionFromSub(sub: string): string | undefined {
    return inferAuthConnectionFromSub(sub);
  }

  async rehydrateSession(storedAuthConnection?: string): Promise<DashboardAuthUser | null> {
    const resolvedConnection = storedAuthConnection ?? getStoredAuthConnection();

    if (shouldAttemptPasswordlessRenewal(resolvedConnection)) {
      const passwordlessToken = await getValidPasswordlessIdToken(resolvedConnection);
      if (!passwordlessToken) {
        return null;
      }
      const authConnection = resolvedConnection ?? AUTH_CONNECTION.EMAIL_PASSWORDLESS;
      return buildDashboardAuthUserFromIdToken(authConnection, passwordlessToken);
    }

    const client = await getAuth0Client();
    try {
      await client.checkSession();
      if (await client.isAuthenticated()) {
        const authUser = await client.getUser();
        let claims = await client.getIdTokenClaims();
        if (claims?.__raw && isStaleIssuer(claims.__raw)) {
          claims = await refreshStaleSocialIdTokenClaims();
        }
        if (authUser && claims?.sub) {
          // Silent rehydration has no appState; resolvedConnection comes from shared storage.
          const authConnection = resolveAuthConnection(undefined, resolvedConnection, claims.sub);
          if (authConnection) {
            return buildDashboardAuthUser(authConnection, authUser, claims);
          }
        }
      }
    } catch (error) {
      // SPA silent auth failed (e.g. login_required, third-party cookies blocked).
      log.error("rehydrateSession failed:", error);
    }
    return null;
  }

  /** Bootstrap passwordless auto-renew: schedule renewal before exp and re-check on tab focus. */
  initPasswordlessAutoRenew(): void {
    initPasswordlessAutoRenew();
  }
}

const auth0Handler = new Auth0Handler();
export default auth0Handler;
